import { hexLen, hexToWorld, worldToHex, type Hex } from '../hex.ts';
import { idiv } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';
import { naveTrace, type NaveLayout } from './ermita-layout.ts';

/**
 * The cleric's last road, chapter 5 (docs/sendas/clerigo.md, «La vigilia»): what its fights stand on, the same from the
 * seed in the sim and the drawing, integers only. Up is −y (the top of the screen).
 *
 * - The nave (the lamps, the mirrors again, the Doliente) is chapter 4's own (battle/ermita-layout.ts `naveLayout`), the
 *   one of the try won: `vigilPlaces` puts the candle by Tessa on the altar, each of the path's people where they keep,
 *   the tongues' two ways down the sides and where the ones who come wait past the atrium.
 * - `renaveStart`: the mirrors again (§4): the way that lit both in chapter 4, three of its pieces turned by the wind
 *   (the seed says which and how), lighting neither.
 * - `atriumLayout`: the atrium of the Hermitage, a hall cut in the rock between the cliff's door (south, where the
 *   procession comes in) and the nave's (north, Piet barring it): four pillars, the cells' doors in the long walls.
 */

export const ATRIUM_RADIUS = 8;

type Pt = { x: number; y: number };

/** Where chapter 5's people keep in the nave, the candle, the tongues' ways and the waiting place of the ones who come. */
export interface VigilPlaces {
  candle: Pt;
  /** Where the hero stands to light each lamp (beside it, on the floor). */
  stands: Pt[];
  keeps: { id: string; x: number; y: number }[];
  /** Down each side of the nave, from the door's end to the altar's. */
  lanes: Pt[][];
  roof: Pt;
  /** Past the atrium's door (out of sight), and where they come to (by the nave's door). */
  away: Pt;
}

export const vigilPlaces = (L: NaveLayout): VigilPlaces => {
  const a = hexToWorld(L.altar.q, L.altar.r);
  const d = hexToWorld(L.door.q, L.door.r);
  // (Toward the nave's axis from the altar: the lamp of the altar stands that side, the candle between it and Tessa.)
  const sgn = a.x > 0 ? -1 : 1;
  const candle = { x: a.x + sgn * 500, y: a.y + 1000 };
  const keeps = [
    { id: 'tessa', x: L.rest.x, y: L.rest.y },
    { id: 'joran', x: candle.x + sgn * 650, y: candle.y + 350 },
    { id: 'piet', x: d.x + 650, y: d.y - 700 },
    { id: 'apprenticeA', x: d.x - 700, y: d.y - 650 },
    { id: 'apprenticeB', x: d.x + 1400, y: d.y - 1100 },
    { id: 'ludo', x: d.x - 300, y: d.y - 1700 },
    { id: 'arriero', x: d.x - 1500, y: d.y - 1300 },
    { id: 'arrieroB', x: d.x + 1600, y: d.y - 2000 },
  ];
  const lanes = [-1, 1].map((side) => [
    { x: side * 2600, y: d.y - 1800 },
    { x: side * 2600, y: 0 },
    { x: side * 2400, y: a.y + 2200 },
  ]);
  const lc = hexToWorld(L.lantern.q, L.lantern.r);
  const rock = (p: Pt): boolean => {
    const h = worldToHex(p.x, p.y);
    return L.rocks.some((x) => x.q === h.q && x.r === h.r) || (h.q === L.lantern.q && h.r === L.lantern.r);
  };
  // Beside each lamp toward the nave's axis; else the other side, below or above it (never on a column).
  const stands = L.lamps.map((l) => {
    const toward = l.x > 0 ? -450 : 450;
    const tries = [
      { x: l.x + toward, y: l.y + 200 },
      { x: l.x - toward, y: l.y + 200 },
      { x: l.x, y: l.y + 650 },
      { x: l.x, y: l.y - 650 },
      { x: l.x + toward, y: l.y + 700 },
    ];
    return tries.find((p) => !rock(p)) ?? (tries[2] as Pt);
  });
  return { candle, stands, keeps, lanes, roof: { x: lc.x, y: lc.y }, away: { x: d.x, y: d.y + 2600 } };
};

/**
 * The mirrors as the wind left them (§4, «Los espejos otra vez»): the answer, `moved` of the pieces on its way turned to
 * another position, so that neither the altar nor the door is lit. Dividers among them too.
 */
export const renaveStart = (L: NaveLayout, seed: number, moved: number): { turns: number[]; splits: number[] } => {
  const rng = seedRng(deriveSeed(seed, 0x5e77));
  const pieces: number[] = [];
  for (let i = 0; i < 8; i++) if ((L.answer.touched >> i) & 1 && i !== L.aud) pieces.push(i);
  const B = { lantern: L.lantern, altar: L.altar, door: L.door, mirrors: L.mirrors, splits: L.splits };
  for (let attempt = 0; attempt < 80; attempt++) {
    const turns = [...L.answer.turns];
    const splits = [...L.answer.splits];
    const pool = [...pieces];
    for (let k = 0; k < moved && pool.length; k++) {
      const i = pool.splice(rngInt(rng, pool.length), 1)[0] as number;
      if (i >= 6) splits[i - 6] = 1 - (splits[i - 6] ?? 0);
      else turns[i] = ((turns[i] ?? 0) + 1 + rngInt(rng, 2)) % 3;
    }
    const b = naveTrace(B, turns, splits);
    if (!b.altar && !b.door) return { turns, splits };
  }
  // (Never: three pieces off the only way that lights both can't light both; this keeps it safe all the same.)
  const turns = L.answer.turns.map((t, i) => ((L.answer.touched >> i) & 1 ? (t + 1) % 3 : t));
  return { turns, splits: [...L.answer.splits] };
};

/** The atrium (act 2): its walls and pillars, its two doors, where the cells open, where its people keep. */
export interface AtriumLayout {
  radius: number;
  rocks: Hex[];
  door: Pt;
  gate: Pt;
  cells: Pt[];
  pillars: Hex[];
  keeps: { id: string; x: number; y: number }[];
  start: Pt;
  /** The nave's door's cell and the cliff's (for the drawing). */
  doorHex: Hex;
  gateHex: Hex;
}

/** Inside the atrium: seven rows each side of its middle, three and a half hexes each side of its axis. */
export const inAtrium = (q: number, r: number): boolean => r >= -7 && r <= 7 && Math.abs(2 * q + r) <= 7;

export const atriumLayout = (seed: number): AtriumLayout => {
  void seed;
  const R = ATRIUM_RADIUS;
  const pillars: Hex[] = [
    { q: 4, r: -4 },
    { q: 0, r: -4 },
    { q: 1, r: 2 },
    { q: -3, r: 2 },
  ];
  const doorHex = { q: 4, r: -7 };
  const gateHex = { q: -4, r: 7 };
  const open = [doorHex, gateHex, { q: -4, r: 8 }, { q: -5, r: 8 }];
  const rocks: Hex[] = [];
  for (let r = -R; r <= R; r++)
    for (let q = -R; q <= R; q++) {
      if (hexLen(q, r) > R) continue;
      const h = { q, r };
      if (open.some((o) => o.q === q && o.r === r)) continue;
      if (!inAtrium(q, r) || pillars.some((p) => p.q === q && p.r === r)) rocks.push(h);
    }
  const dw = hexToWorld(doorHex.q, doorHex.r);
  const gw = hexToWorld(gateHex.q, gateHex.r);
  const cl = hexToWorld(-3, 0);
  const cr = hexToWorld(3, 0);
  const mid = hexToWorld(2, -2);
  return {
    radius: R,
    rocks,
    door: { x: dw.x, y: dw.y },
    gate: { x: gw.x, y: gw.y + 300 },
    cells: [
      { x: cl.x - 300, y: cl.y },
      { x: cr.x + 300, y: cr.y },
    ],
    pillars,
    keeps: [
      { id: 'piet', x: dw.x, y: dw.y - 650 },
      { id: 'joran', x: mid.x, y: mid.y },
      { id: 'apprenticeA', x: mid.x - 1400, y: mid.y + 300 },
      { id: 'apprenticeB', x: mid.x + 1300, y: mid.y + 200 },
    ],
    start: { x: dw.x - 200, y: dw.y + 1500 },
    doorHex,
    gateHex,
  };
};

/** A brazier by a mirror's column (the Doliente's frost), toward the nave's axis from where the hero turns it. */
export const brazierBy = (L: NaveLayout, mirror: number): Pt => {
  const m = L.mirrors[mirror] as Hex;
  const c = hexToWorld(m.q, m.r);
  const st = L.mirrorStand[mirror] as Pt;
  return { x: st.x + idiv((st.x - c.x) * 6, 10), y: st.y + idiv((st.y - c.y) * 6, 10) + 300 };
};
